import React from 'react';

import { SailingDay, Sailing } from '../../shared/types/sailing';
import { getLocaleDateStringUTC } from '../../shared/lib/time';
import { getDayLocation } from '../../shared/lib/sailing';
import { Template, SailingTemplate, BareTemplate } from '../../shared/types/template';
import {
  getDayTemplates,
  cloneSailingTemplate,
  editSailingTemplate,
  deleteTemplate as requestDeleteTemplate,
  populateDayTemplate,
} from '../lib/api/template';
import {
  TabPanel,
  TabContent,
  RadioButtonGroup,
  Header,
  RadioButton,
  TextButton,
  InlineIcon,
  Button,
} from '@spark-labs/avocado-ui';
import { toast } from './UIkit/Toast/Toast';

export interface SailingDayTemplateFormProps {
  sailing: Sailing;
  sailingDay: SailingDay;
  sailingTemplates: SailingTemplate[];
  onSubmit?: (sailing: Sailing, sailingDay: SailingDay, template: Template) => void;
  // We don't want to apply a new template, so we don't use the onSubmit,
  // ideally this should happen on popup close tho...
  onTemplateNameUpdate?: () => void;
}

type EditableTemplate = Template & { isEditing: boolean };
export interface SailingDayTemplateFormState {
  selectedTab: number;
  selectedTemplate?: Template;
  dayTemplates: EditableTemplate[];
  selectedTemplateNewName: string;
}

export default class SailingDayTemplateForm extends React.Component<
  SailingDayTemplateFormProps,
  SailingDayTemplateFormState
> {
  public readonly state: SailingDayTemplateFormState = {
    selectedTab: 0,
    dayTemplates: [],
    selectedTemplateNewName: '',
  };

  constructor(props: SailingDayTemplateFormProps) {
    super(props);

    this.handleSelectedTab = this.handleSelectedTab.bind(this);
    this.handlePlanTemplateSelect = this.handlePlanTemplateSelect.bind(this);
    this.handleSailingDayTemplateSelect = this.handleSailingDayTemplateSelect.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  public async componentDidMount() {
    this.setState({
      dayTemplates: await this.getTemplates(),
    });
  }

  public render() {
    let dayIndex = this.props.sailing.days.findIndex(d => d._id === this.props.sailingDay._id);
    // If for some reason we cannot find the day in the sailing, use the day's name
    // This should not happen, though!
    let dayNumber = dayIndex === -1 ? this.props.sailingDay.name : dayIndex + 1;

    return (
      <div className="border-t border-grey pt-2 p-4 mt-6" style={{ width: '600px' }}>
        <div className="text-xl text-teal-darker">
          <span>Day of Trip: </span>
          <span className="font-bold text-2xl mx-1">{dayNumber}</span>
          <span className="mx-4">|</span>
          <span>Starting Date: </span>
          <span className="font-bold text-2xl mx-1">
            {getLocaleDateStringUTC(this.props.sailingDay.date)}
          </span>
        </div>
        <div className="text-xl text-teal-darker mt-2">
          <span>Location: </span>
          <span className="font-bold text-2xl mx-1">{getDayLocation(this.props.sailingDay)}</span>
        </div>
        <TabPanel selectedTab={this.state.selectedTab} onTabChange={this.handleSelectedTab}>
          <TabContent title="SET Projects">
            <div className="p-4">
              <RadioButtonGroup
                name="plan-day-templates"
                onChange={this.handlePlanTemplateSelect}
                groupValue={
                  this.state.selectedTemplate ? this.state.selectedTemplate._id : undefined
                }
              >
                {groupProps => (
                  <div className="overflow-auto" style={{ maxHeight: 300 }}>
                    {this.props.sailingTemplates.map(template => (
                      <div key={template._id}>
                        <Header size="sm">{template.name}</Header>
                        <ul>
                          {template.dayTemplates.map(dayTemplate => (
                            <li key={dayTemplate._id} className="my-1">
                              <RadioButton
                                value={dayTemplate._id}
                                label={dayTemplate.name}
                                checked={groupProps.groupValue === dayTemplate._id}
                                {...groupProps}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </RadioButtonGroup>
            </div>
          </TabContent>
          <TabContent title="GO Favorite Days">
            <div className="p-4">
              <RadioButtonGroup
                name="sailing-day-templates"
                onChange={this.handleSailingDayTemplateSelect}
                groupValue={
                  this.state.selectedTemplate ? this.state.selectedTemplate._id : undefined
                }
              >
                {groupProps => (
                  <div className="overflow-auto" style={{ maxHeight: 300 }}>
                    <ul>
                      {this.state.dayTemplates.map((dayTemplate, i) => (
                        <li
                          style={{ height: '25px' }}
                          key={dayTemplate._id}
                          className="my-1 inline-flex justify-between items-center w-full"
                        >
                          <RadioButton
                            value={dayTemplate._id}
                            label={''}
                            checked={groupProps.groupValue === dayTemplate._id}
                            {...groupProps}
                          />
                          {dayTemplate.isEditing ? (
                            <span className="w-full h-full">
                              <input
                                value={this.state.selectedTemplateNewName}
                                onChange={e =>
                                  this.setState({ selectedTemplateNewName: e.target.value })
                                }
                                className="w-4/5 mr-auto px-1 py-0 border border-grey h-full"
                              />
                              <span className="ml-1">
                                <Button
                                  color="blue"
                                  onClick={
                                    /**
                                     * FIXME: Pending fix of `getDayTemplates` to return the correct
                                     * type
                                     */
                                    () => this.handleTemplateNameSave(dayTemplate as any)
                                  }
                                >
                                  Save
                                </Button>
                              </span>
                            </span>
                          ) : (
                            <label className="w-full">{dayTemplate.name}</label>
                          )}

                          <span className="w-1/4 ml-auto inline-flex justify-end">
                            <TextButton
                              className="text-grey-dark mr-2"
                              onClick={() => this.toggleEditing(i)}
                            >
                              <InlineIcon name={dayTemplate.isEditing ? 'close' : 'edit'} />
                            </TextButton>
                            <TextButton
                              className="text-grey-dark mr-2"
                              onClick={() => this.cloneSailingDayTemplate(dayTemplate as any)}
                            >
                              <InlineIcon name="copy" />
                            </TextButton>
                            <TextButton
                              className="text-grey-dark mr-2 no-underline"
                              onClick={() => this.deleteTemplate(dayTemplate as any)}
                            >
                              <InlineIcon name="trash" />
                            </TextButton>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </RadioButtonGroup>
            </div>
          </TabContent>
        </TabPanel>
        <div className="mt-4 flex justify-end">
          <div className="mx-2">
            <Button
              color="green"
              onClick={this.handleFormSubmit}
              disabled={!this.state.selectedTemplate}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  private handleSelectedTab(tabIndex: number) {
    this.setState({ selectedTab: tabIndex });
  }

  private handlePlanTemplateSelect(templateId: string) {
    for (let sailingTemplate of this.props.sailingTemplates) {
      for (let dayTemplate of sailingTemplate.dayTemplates) {
        if (dayTemplate._id === templateId) {
          return this.setState({ selectedTemplate: dayTemplate });
        }
      }
    }
    console.error(`Could not find template ${templateId}`);
  }

  private handleSailingDayTemplateSelect(templateId: string) {
    let selectedTemplate = this.state.dayTemplates.find(dt => dt._id === templateId);
    return this.setState({ selectedTemplate });
  }

  private handleFormSubmit() {
    if (!this.state.selectedTemplate || !this.props.onSubmit) {
      return;
    }
    this.props.onSubmit(this.props.sailing, this.props.sailingDay, this.state.selectedTemplate);
  }

  private getTemplates = async (): Promise<EditableTemplate[]> => {
    const bareTemplates = await getDayTemplates(this.props.sailing.property._id, [
      'sailing',
      'template',
    ]);
    const templates = bareTemplates.map(bt => populateDayTemplate(bt, this.props.sailing.property));
    return templates.map(temp => ({ ...temp, isEditing: false }));
  };

  private deleteTemplate = async (template: BareTemplate) => {
    await requestDeleteTemplate(template.property, template._id);

    await toast.success('Template deleted successfully');

    this.setState({
      dayTemplates: await this.getTemplates(),
    });
  };

  private cloneSailingDayTemplate = async (template: BareTemplate) => {
    await cloneSailingTemplate(template.property, template._id);

    this.setState({
      dayTemplates: await this.getTemplates(),
    });
  };

  private toggleEditing = (dayTemplateIndex: number) => {
    this.setState({
      dayTemplates: this.state.dayTemplates.map((temp, i) => {
        return { ...temp, isEditing: i === dayTemplateIndex ? !temp.isEditing : temp.isEditing };
      }),
      selectedTemplateNewName: this.state.dayTemplates[dayTemplateIndex].name,
    });
  };

  private handleTemplateNameSave = async (template: BareTemplate) => {
    await editSailingTemplate(template.property, template._id, {
      ...template,
      name: this.state.selectedTemplateNewName,
    });

    await toast.success('Template name edited');

    this.setState({ dayTemplates: await this.getTemplates() });
    if (this.props.onTemplateNameUpdate) {
      this.props.onTemplateNameUpdate();
    }
  };
}
